"use client";

import { CSSProperties, useState } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    AccordionAppearance,
    AccordionBlock as AccordionBlockType,
    AccordionItem,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import {
    resolveAccordionAppearance,
    createAccordionItem,
} from "../../../lib/blockRegistry";
import RichText from "../text/richText";
import { stripHtml, wrapInTag } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import RichHtml from "./richHtml";
import { IndexBadge } from "../shared/IndexBadge";
import { CompletionCheck } from "../shared/CompletionCheck";
import { INDEX_ICON } from "../shared/indexIcon";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

const cardStyle = (a: AccordionAppearance): CSSProperties => ({
    backgroundColor: a.cardBackground,
    borderColor: a.border,
    borderWidth: a.borderWidth,
    borderStyle: "solid",
});


function PreviewAccordion({
    items,
    appearance,
    gridGap,
}: {
    items: AccordionItem[];
    appearance: AccordionAppearance;
    gridGap: number;
}) {
    const locale = useLomdaLocale();
    const [openId, setOpenId] = useState<string | null>(null);
    const [seen, setSeen] = useState<Set<string>>(new Set());

    const toggle = (id: string) => {
        setOpenId((cur) => (cur === id ? null : id));
        setSeen((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

    return (
        <div className="flex flex-col" style={{ gap: gridGap }}>
            {items.map((it, i) => {
                const open = openId === it.id;
                const showCheck = appearance.showCheckmark && seen.has(it.id);
                return (
                    <div
                        key={it.id}
                        className="lomda-v3-card-text overflow-hidden rounded-xl"
                        style={cardStyle(appearance)}
                    >
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => toggle(it.id)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggle(it.id);
                                }
                            }}
                            className="flex w-full cursor-pointer items-center gap-4 p-5 text-start"
                        >
                            <IndexBadge size={INDEX_ICON.badgeSize}>
                                {i + 1}
                            </IndexBadge>
                            {stripHtml(it.title) ? (
                                <RichHtml
                                    html={it.title}
                                    className="min-w-0 flex-1"
                                />
                            ) : (
                                <span className="lomda-v3-text-h5 min-w-0 flex-1 text-muted-foreground">
                                    {t(
                                        "lomda-v3-editor.accordion.untitled",
                                        locale
                                    )}
                                </span>
                            )}
                            {showCheck && <CompletionCheck />}
                            <ChevronDown
                                className={cn(
                                    "size-6 shrink-0 text-muted-foreground transition-transform duration-300",
                                    open && "rotate-180"
                                )}
                            />
                        </div>
                        <div
                            className={cn(
                                "grid transition-all duration-300",
                                open
                                    ? "grid-rows-[1fr] opacity-100"
                                    : "grid-rows-[0fr] opacity-0"
                            )}
                        >
                            <div className="overflow-hidden">
                                <div className="px-5 pb-5 ps-14">
                                    <RichHtml html={it.body} />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function AccordionBlock({
    block,
}: {
    block: AccordionBlockType;
}) {
    const titleTag = fieldTag("accordion", "title");
    const bodyTag = fieldTag("accordion", "body");
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const clearActiveEditor = useLomdaV3Editor((s) => s.clearActiveEditor);

    const editable = useBlockEditable();
    const gridGap = useResolvedGridGap(block.id);
    const { items } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveAccordionAppearance(block.data.appearance, theme);

    const update = (patch: Partial<AccordionBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const setItems = (next: AccordionItem[]) => update({ items: next });
    const patchItem = (id: string, patch: Partial<AccordionItem>) =>
        setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    if (!editable) {
        const filled = items.filter(
            (it) => stripHtml(it.title) || stripHtml(it.body)
        );
        if (filled.length === 0) return null;
        return (
            <PreviewAccordion
                items={filled}
                appearance={appearance}
                gridGap={gridGap}
            />
        );
    }

    return (
        <div
            onPointerDownCapture={(e) => {
                if (
                    !(e.target as HTMLElement).closest?.(".lomda-v3-rich-text")
                ) {
                    clearActiveEditor();
                }
            }}
            className="flex flex-col"
            style={{ gap: gridGap }}
        >
            {items.map((it, i) => (
                <div
                    key={it.id}
                    className="lomda-v3-card-text flex flex-col gap-2 rounded-xl p-4"
                    style={cardStyle(appearance)}
                >
                    <div className="flex items-center gap-4">
                        <IndexBadge size={INDEX_ICON.badgeSize}>
                            {i + 1}
                        </IndexBadge>
                        <div className="min-w-0 flex-1">
                            <RichText
                                initialHtml={it.title || wrapInTag(titleTag)}
                                defaultTag={titleTag}
                                editable
                                useGlobalToolbar
                                blockId={block.id}
                                groupKey={`${block.id}:accordion-title`}
                                placeholder={t(
                                    "lomda-v3-editor.accordion.title-placeholder"
                                )}
                                onCommit={(title) =>
                                    patchItem(it.id, { title })
                                }
                                
                            />
                        </div>
                        <button
                            type="button"
                            aria-label={t("lomda-v3-editor.block.delete")}
                            onClick={() =>
                                setItems(items.filter((x) => x.id !== it.id))
                            }
                            className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-destructive"
                        >
                            <Trash2 className="size-4" />
                        </button>
                    </div>
                    <div className="ps-9">
                        <RichText
                            initialHtml={it.body || wrapInTag(bodyTag)}
                            defaultTag={bodyTag}
                            editable
                            useGlobalToolbar
                            blockId={block.id}
                            groupKey={`${block.id}:accordion-body`}
                            placeholder={t(
                                "lomda-v3-editor.accordion.body-placeholder"
                            )}
                            onCommit={(body) => patchItem(it.id, { body })}
                        />
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={() => setItems([...items, createAccordionItem()])}
                className="lomda-v3-chrome flex items-center justify-center gap-1.5 rounded-xl border border-dashed py-2.5 text-sm text-muted-foreground transition hover:border-primary hover:text-primary"
            >
                <Plus className="size-4" />
                {t("lomda-v3-editor.accordion.add")}
            </button>
        </div>
    );
}
